import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  OnyxButtonComponent,
  OnyxIconComponent,
  OnyxModalComponent,
  OnyxSpinnerComponent,
} from '@onyx/angular';
import type { Observable } from 'rxjs';
import { EMPTY, map, of, Subject, switchMap } from 'rxjs';
import { ImportRouterStateKey } from '../../../enums/import-router-state-key';
import type {
  ImportParseProgress,
  ImportParseResult,
} from '../../../interfaces/import-parse-progress';

export interface ImportModalData {
  name: string;
  originRoute: string;
  resultRoute: string;
  result$: Observable<ImportParseProgress<unknown>>;
}

@Component({
  selector: 'app-import-modal',
  imports: [
    OnyxModalComponent,
    AsyncPipe,
    OnyxSpinnerComponent,
    TranslatePipe,
    OnyxIconComponent,
    OnyxButtonComponent,
  ],
  templateUrl: './import-modal.component.html',
  styleUrl: './import-modal.component.scss',
})
export class ImportModalComponent {
  protected readonly dialogRef = inject(DialogRef);
  private readonly data = inject<ImportModalData>(DIALOG_DATA);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);

  protected readonly I18N = 'import.modal';

  protected result$ = this.data.result$.pipe(
    switchMap((result) => {
      if (!result.loading && result.ok) {
        this.import(result.data);
        return EMPTY;
      }
      return of(result);
    }),
    map((result) => {
      if (result.loading) {
        return {
          progress: result.progress,
          total: result.total,
        };
      }

      return {
        heading: this.translateService.instant(`${this.I18N}.errorHeading`, {
          count: result.errors.length,
        }),
        errors: result.errors,
        data: result.data,
      };
    }),
    takeUntilDestroyed(this.destroyRef),
  );
  protected close$ = new Subject<void>();

  protected import(data: ImportParseResult<unknown>['data']): void {
    this.router.navigateByUrl(this.data.resultRoute, {
      state: {
        [ImportRouterStateKey.ORIGIN_ROUTE]: this.data.originRoute,
        [ImportRouterStateKey.NAME]: this.data.name,
        [ImportRouterStateKey.DATA]: data,
      },
    });
    this.close$.next();
  }
}
